import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import {
  playNotificationSound,
  requestNotificationPermission,
  showBrowserNotification,
  isPageHidden,
} from '../utils/notifications';
import { pushAPI } from '../services/api';

const SocketContext = createContext(null);

// Convert VAPID key from base64 to Uint8Array (required by PushManager)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export const SocketProvider = ({ children }) => {
  const { user } = useAuth();
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);

  // Request notification permission + register push on login
  useEffect(() => {
    if (!user) return;
    requestNotificationPermission();

    // Register service worker and subscribe to push
    const registerPush = async () => {
      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

        const reg = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;

        const { data } = await pushAPI.getVapidKey();
        const vapidKey = data.data?.publicKey;
        if (!vapidKey) return;

        // Check existing subscription
        let subscription = await reg.pushManager.getSubscription();
        if (!subscription) {
          subscription = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey),
          });
        }

        // Send subscription to backend
        await pushAPI.subscribe(subscription.toJSON());
      } catch (err) {
        console.log('Push registration skipped:', err.message);
      }
    };
    registerPush();
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!user || !token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setConnected(false);
      }
      return;
    }

    const socket = io(process.env.REACT_APP_SOCKET_URL || window.location.origin, {
      auth: { token },
      transports: ['polling', 'websocket'],  // polling first — works through nginx without WS config; upgrades to WS after handshake
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    socket.on('connect', () => { setConnected(true); });
    socket.on('disconnect', () => { setConnected(false); });
    socket.on('connect_error', (err) => { console.error('Socket error:', err.message); });

    // New message notification
    socket.on('message:new', (payload) => {
      const { message, customer } = payload;
      // Only notify for inbound messages (from customers)
      if (message?.direction !== 'inbound') return;

      const name = customer?.name || 'Customer';
      const text = message?.content?.text || 'New message';

      // Always play sound
      playNotificationSound();

      // Show browser notification if page is hidden (minimized/different tab)
      if (isPageHidden()) {
        showBrowserNotification(
          `New message from ${name}`,
          text.length > 80 ? text.slice(0, 80) + '...' : text,
          () => { window.focus(); }
        );
      }

      // Update page title with unread indicator
      if (isPageHidden()) {
        document.title = `(New) ${name} - AINA`;
      }
    });

    // Reset title when page becomes visible
    const handleVisibility = () => {
      if (!document.hidden) {
        document.title = 'AINA';
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);

  const on = (event, handler) => socketRef.current?.on(event, handler);
  const off = (event, handler) => socketRef.current?.off(event, handler);
  const emit = (event, data) => socketRef.current?.emit(event, data);
  const joinConversation = (customerId) => emit('join:conversation', customerId);
  const leaveConversation = (customerId) => emit('leave:conversation', customerId);

  return (
    <SocketContext.Provider value={{ socket: socketRef.current, connected, on, off, emit, joinConversation, leaveConversation }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
